/**
 * Глобальное состояние чата. Чат смонтирован ОДИН раз на уровне App —
 * навигация между страницами (нарратив ↔ план ↔ отчёты ↔ сравнение) не
 * анмаунтит его и не рвёт стрим: агент думает, пользователь изучает модель.
 *
 * Страницы регистрируют свой контекст (owner/repo/ref/path/pageContext)
 * через setCtx — транспорт чата читает его лениво на каждый запрос.
 *
 * unread — «ответ готов»: стрим завершился, пока чат был свёрнут;
 * индикатор на FAB, сбрасывается открытием.
 */

import { create } from 'zustand';
import type { ChatPageContext } from '@pflow/shared/v2';

export interface ChatPageCtx {
    owner:        string;
    repo:         string;
    gitRef?:      string;
    path?:        string;
    pageContext?: ChatPageContext;
}

interface ChatStoreState {
    ctx:    ChatPageCtx | null;
    open:   boolean;
    unread: boolean;

    setCtx:    (ctx: ChatPageCtx) => void;
    setOpen:   (open: boolean) => void;
    setUnread: (unread: boolean) => void;
}

export const useChatStore = create<ChatStoreState>(set => ({
    ctx:    null,
    open:   false,
    unread: false,

    setCtx:  ctx  => set({ ctx }),
    setOpen: open => set(s => ({ open, unread: open ? false : s.unread })),
    setUnread: unread => set({ unread }),
}));

/** Хук для страниц: зарегистрировать контекст чата (в useEffect). */
export function registerChatContext(ctx: ChatPageCtx): void {
    const cur = useChatStore.getState().ctx;
    // Не дёргаем стор без изменений (страницы зовут из useEffect на каждый рендер).
    if (cur
        && cur.owner === ctx.owner && cur.repo === ctx.repo
        && cur.gitRef === ctx.gitRef && cur.path === ctx.path
        && JSON.stringify(cur.pageContext) === JSON.stringify(ctx.pageContext)) return;
    useChatStore.getState().setCtx(ctx);
}
